import styled from 'styled-components';
import { COLORS } from '../../../constants/radar';
import { BAR_COLORS, getCompletionRateColor } from '../../../constants/assignmentCompletion';

export const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 24px;
`;

export const HeaderStats = styled.div`
  display: flex;
  align-items: flex-start;
`;

export const HeaderStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
  position: relative;
  padding: 0 16px;
  min-height: 36px;
  /* Each stat carries the same definition as its grid column (ADR-014 §10i) */
  cursor: help;

  .top {
    display: flex;
    align-items: baseline;
    gap: 6px;
  }

  .label {
    font-size: 11px;
    color: #64748B;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }

  .value {
    font-size: 20px;
    font-weight: 700;
    color: ${COLORS.PRIMARY};
  }

  .sub {
    font-size: 9px;
    color: #94A3B8;
    line-height: 1;
  }

  & + &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
    height: 24px;
    background: #e0e0e0;
  }
`;

// BU | cluster | Measured | Skipped | Expected | Open | Completed | Missing | % Completed
//
// Skipped sits beside Measured, not in the run of work columns: it is a
// fact about PEOPLE, and "19 counted, 4 skipped" is one thought. Between
// Missing and % Completed it interrupted the arithmetic chain
// (Expected → its breakdown → the ratio) right before its conclusion.
const GRID_COLUMNS = '1.5fr 0.9fr 0.6fr 0.6fr 0.6fr 1.3fr 1.3fr 0.6fr 1.5fr';

// The trailing % column renders [bar][count] (headline number at the row's
// end), so its BAR edge faces the Missing column's right-aligned number.
// A wider seam there keeps that number from reading as the % bar's label —
// and marks % Completed as the derived column it is.
const RATE_COLUMN_SEAM = '20px';

// Bands above the header naming WHAT each run of columns counts.
//
// Measured and Skipped count PEOPLE; Expected, Open, Completed and Missing
// count ASSIGNMENTS. Seven right-aligned numbers in a row give the eye no way
// to see where the unit changes, so the bands draw that boundary once instead
// of asking each header to imply it. Naming both — rather than only the
// assignments run — is what makes the contrast explicit; a single band leaves
// the reader to infer that everything outside it is something else.
//
// % Completed sits outside both spans deliberately: it is derived FROM the
// assignment columns, not another count alongside them.
export const GroupHeader = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  gap: 28px;
  align-items: end;
  padding: 0 16px;

  /* Every span is expressed in grid LINES over the shared template, so a band
     can never drift from the columns it labels the way a fixed width would. */
  .spacer {
    grid-column: 1 / 3;
  }

  .group {
    text-align: center;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    color: #94A3B8;
    padding-bottom: 4px;
    border-bottom: 1px solid #e2e8f0;

    &.people {
      grid-column: 3 / 5;
    }

    &.assignments {
      grid-column: 5 / 9;
    }
  }
`;

export const GridHeader = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  gap: 28px;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 2px solid #e2e8f0;

  > *:last-child {
    padding-left: ${RATE_COLUMN_SEAM};
  }

  span {
    font-size: 11px;
    color: #64748B;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
    white-space: nowrap;

    &.num {
      text-align: right;
    }

    /* Signals that the header explains itself on hover. The dotted underline
       tracks the text, not the grid cell, so it stays with the label whether
       the column aligns it left or right. */
    &.help {
      cursor: help;
      text-decoration: underline dotted #CBD5E1;
      text-underline-offset: 3px;
    }
  }
`;

export const SuRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  gap: 28px;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.15s;

  /* Matches GridHeader so the seam and the header stay in one column */
  > *:last-child {
    padding-left: ${RATE_COLUMN_SEAM};
  }

  &:hover {
    background-color: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }

  .su-name {
    font-size: 14px;
    font-weight: 600;
    color: ${COLORS.PRIMARY};
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;

    .warn {
      color: #F59E0B;
      font-size: 12px;
      flex-shrink: 0;
    }

    /* An unpolicied unit is a configuration gap, not a failure — muted */
    .tag {
      font-size: 9px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      color: #64748B;
      background: #F1F5F9;
      border: 1px solid #E2E8F0;
      border-radius: 10px;
      padding: 1px 6px;
      white-space: nowrap;
      cursor: help;
      flex-shrink: 0;
    }

    /* Drill-down into the BU's individuals (ADR-094) */
    .link {
      cursor: pointer;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:hover {
        text-decoration: underline;
        text-underline-offset: 2px;
      }
    }
  }

  .dc-name {
    font-size: 12px;
    color: #64748B;
  }

  .num {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    text-align: right;

    &.muted {
      color: #94A3B8;
      font-weight: 400;
    }

    /* Skipped sits outside every calculation — a distinct weight/colour so it
       never reads as part of the completion arithmetic beside it.
       NO cursor:help or dotted underline here: those signal "hover me for an
       explanation", and the explanation lives on the column header like every
       other column's. The cell once had its own tooltip (a per-reason
       breakdown); when Skipped narrowed to new joiners that content became a
       duplicate of the header's, so the affordance had to go with it. */
    &.skipped {
      color: #64748B;
      font-weight: 500;
    }
  }
`;

// A numeric cell paired with a proportional bar (mock treatment).
//
// Number↔bar binding rule (review feedback 2026-08-11): a count always
// TOUCHES its own bar — interior columns render [count][bar] (number
// immediately left of its bar), the trailing % column renders [bar][count]
// (headline number at the row's end). The wide 28px grid gap then separates
// PAIRS, never a number from its bar, so ownership is unambiguous. The JSX
// controls the order; this style handles both.
export const BarCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  .track {
    flex: 1;
    height: 12px;
    background: ${BAR_COLORS.TRACK};
    border-radius: 6px;
    overflow: hidden;
  }

  .fill {
    height: 100%;
    border-radius: 6px;
    background: ${({ $color }) => $color};
    width: ${({ $pct }) => Math.max(0, Math.min(100, $pct || 0))}%;
    transition: width 0.2s;
  }

  .count {
    min-width: 36px;
    text-align: right;
    font-size: 13px;
    font-weight: 600;
    color: #334155;

    &.muted {
      color: #94A3B8;
      font-weight: 400;
    }
  }
`;

// ADR-098: the Open cell, made to open. A number in a table does not read as
// a button, so the affordance appears where the eye already is — the count
// gains a caret and an underline on row hover, and the cell tints while its
// panel is open. Rendered as a real <button> for keyboard and screen readers;
// zero Open is not interactive, because an empty panel is a worse answer than
// no panel.
export const OpenCell = styled(BarCell).attrs({ as: 'button', type: 'button' })`
  width: 100%;
  background: none;
  border: none;
  padding: 2px 4px;
  margin: -2px -4px;
  border-radius: 4px;
  font: inherit;
  text-align: inherit;
  cursor: pointer;
  transition: background-color 0.15s;

  .caret {
    font-size: 9px;
    color: #CBD5E1;
    flex-shrink: 0;
    transition: transform 0.15s, color 0.15s;
  }

  ${SuRow}:hover & .count {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  ${SuRow}:hover & .caret {
    color: #94A3B8;
  }

  &[aria-expanded='true'] {
    background: #EFF6FF;

    .caret {
      color: ${COLORS.PRIMARY};
      transform: rotate(90deg);
    }
  }
`;

// The panel itself: one line per status, closest to done first, sharing the
// row's grid so each number stays under the column it belongs to.
export const BreakdownRow = styled.div`
  background: #FAFBFC;
  border-bottom: 1px solid #f1f5f9;
  padding: 8px 16px 12px;

  .caption {
    font-size: 11px;
    color: #94A3B8;
    padding-bottom: 6px;
  }

  .lines {
    display: grid;
    /* label | bar | count | frozen — a small table inside the row, indented
       under the BU name so the panel reads as part of the row above it. */
    grid-template-columns: 150px minmax(80px, 220px) 40px auto;
    gap: 4px 12px;
    align-items: center;
    padding-left: 16px;
  }

  .chip {
    justify-self: start;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-radius: 10px;
    padding: 2px 8px;
    white-space: nowrap;
    cursor: help;
  }

  .track {
    height: 8px;
    background: ${BAR_COLORS.TRACK};
    border-radius: 4px;
    overflow: hidden;
  }

  /* Width is per line, so it comes from the element's own style — one shared
     rule here cannot express four different shares. */
  .fill {
    height: 100%;
    border-radius: 4px;
    background: ${BAR_COLORS.NOT_COMPLETED};
  }

  .value {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    text-align: right;

    &.muted {
      color: #CBD5E1;
      font-weight: 400;
    }
  }

  /* Work left incomplete can never move, whatever status it stopped in — so it
     is said beside the status, not counted as one (ADR-098 §3).
     justify-self is load-bearing: a grid item stretches to its column by
     default, so the span's BOX ran to the end of the row while its text sat at
     the left — and MUI anchors a tooltip to the box, which put the tooltip
     several columns away from the words it explains. */
  .frozen {
    justify-self: start;
    font-size: 11px;
    color: #A3AFC0;
    cursor: help;
  }

  /* The open work the cap left out. A sentence, not a footnote: the parts and
     this line together account for every open assignment. */
  .remainder {
    margin-top: 8px;
    padding-left: 16px;
    font-size: 11px;
    color: #A3AFC0;
  }
`;

export const RateCell = styled(BarCell)`
  .count {
    min-width: 44px;
    font-weight: 700;
    color: ${({ $rate }) => getCompletionRateColor($rate)};
  }
`;

export const DataQualityFooter = styled.div`
  margin-top: 12px;
  padding: 10px 16px 12px;
  font-size: 12px;
  color: #94A3B8;
  border-top: 1px dashed #e2e8f0;

  .title {
    font-weight: 600;
    color: #64748B;
    margin-bottom: 6px;
  }

  ul {
    margin: 0;
    padding-left: 16px;
    list-style: disc;
  }

  li {
    line-height: 1.6;
  }

  /* Sub-items break down the count on the line above. Indented, quieter and
     marked with an arrow so the eye never has to work out which total a part
     belongs to — the failure of the previous single-line, dot-separated run. */
  li > ul {
    margin: 2px 0 6px;
    padding-left: 14px;
    list-style: none;
    color: #A3AFC0;
  }

  li > ul > li::before {
    content: '↳';
    margin-right: 6px;
    color: #CBD5E1;
  }
`;

export const SortButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #64748B;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background-color: #f1f5f9;
    color: ${COLORS.PRIMARY};
  }

  svg {
    font-size: 12px;
  }
`;
